<template>
  <Authenticated>
    <div class="navigation-container">
      <div class="header">
        <div :class="['status-panel', gpsStatus === 'GPS Active' ? 'gps-active' : 'gps-inactive']">
          <div class="status-item">
            <span>{{ gpsStatus }}</span>
          </div>
          <div class="status-item">
            <span>Step: {{ currentStepIndex + 1 }}/{{ instructions.length }}</span>
          </div>
          <div class="status-item" v-if="userSpeed > 0">
            <span>Speed: {{ Math.round(userSpeed * 3.6) }} km/h</span>
          </div>
        </div>
        <div class="instruction-panel" v-if="currentInstruction">
          <div class="instruction-icon">
            <font-awesome-icon :icon="getInstructionIcon(currentInstruction.type)" size="2x" />
          </div>
          <div class="instruction-text">
            <h3>{{ currentInstruction.text }}</h3>
            <p>In {{ Math.round(distanceToNext) }}m</p>
          </div>
        </div>
      </div>

      <div id="map" ref="mapContainer" class="map"></div>
    </div>
    <OrderModal :open="showOrderModal" :order="currentOrder" @close="showOrderModal = false" @mark-as-delivered="markAsDelivered" />
  </Authenticated>
</template>

<script>
import Authenticated from "./_layouts/Authenticated.vue";
import L from "leaflet";
import "leaflet-routing-machine";
import "leaflet/dist/leaflet.css";
import { mapGetters } from "vuex";
import OrderModal from "./OrderModal.vue";
import { useGeolocation } from "@vueuse/core";

// Fix default markers for modern bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

export default {
  name: "MapPage",
  components: {
    Authenticated,
    OrderModal,
  },
  setup() {
    const { coords, locatedAt, error, resume, pause } = useGeolocation({
      enableHighAccuracy: true,
      maximumAge: 1000,
      timeout: 10000,
      immediate: false,
    });

    return {
      coords,
      locatedAt,
      geoError: error,
      resumeTracking: resume,
      pauseTracking: pause,
      lastLat: 0.0,
      lastLong: 0.0,
    };
  },
  computed: {
    ...mapGetters({
      selectedOrders: "selectedOrders",
    }),

    currentOrder() {
      return this.selectedOrders.length > 0 ? this.selectedOrders[0] : null;
    },

    userPosition() {
      if (this.coords && this.coords.latitude && this.coords.longitude) {
        return {
          lat: this.coords.latitude,
          lng: this.coords.longitude,
        };
      }
      return { lat: null, lng: null };
    },
  },
  data() {
    return {
      currentInstruction: null,
      instructions: [],
      currentStepIndex: 0,
      distanceToNext: 0,
      gpsStatus: "Waiting for GPS...",
      userSpeed: 0,
      map: null,
      routingControl: null,
      userMarker: null,
      routePolyline: null,
      cityCoords: { lat: -45.871478138862905, lng: 170.50666261494524 }, // Example: City location
      nevCoords: { lat: -45.85481387067378, lng: 170.5191425 }, // Example: NEV location
      showOrderModal: false,
    };
  },
  methods: {
    calculateDistance(lat1, lng1, lat2, lng2) {
      const earthRadiusInMeters = 6371e3;

      const lat1Rad = (lat1 * Math.PI) / 180;
      const lat2Rad = (lat2 * Math.PI) / 180;
      const deltaLatRad = ((lat2 - lat1) * Math.PI) / 180;
      const deltaLngRad = ((lng2 - lng1) * Math.PI) / 180;

      const a = Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLngRad / 2) * Math.sin(deltaLngRad / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return earthRadiusInMeters * c;
    },

    // Calculate bearing between two points for map rotation
    calculateBearing(lat1, lng1, lat2, lng2) {
      const lat1Rad = (lat1 * Math.PI) / 180;
      const lat2Rad = (lat2 * Math.PI) / 180;
      const deltaLngRad = ((lng2 - lng1) * Math.PI) / 180;

      const y = Math.sin(deltaLngRad) * Math.cos(lat2Rad);
      const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLngRad);

      let bearing = Math.atan2(y, x);
      bearing = (bearing * 180) / Math.PI;
      bearing = (bearing + 360) % 360; // Normalize to 0-360

      return bearing;
    },

    snapToRoute(userLat, userLng, routeCoords) {
      let minDistance = Infinity;
      let snappedPoint = { lat: userLat, lng: userLng };

      routeCoords.forEach((coord) => {
        const distance = this.calculateDistance(userLat, userLng, coord.lat, coord.lng);
        if (distance < minDistance && distance < 50) {
          minDistance = distance;
          snappedPoint = coord;
        }
      });

      return snappedPoint;
    },

    getInstructionIcon(type) {
      const iconMap = {
        "turn-right": "arrow-right",
        "turn-left": "arrow-left",
        continue: "arrow-up",
        "turn-slight-right": "chevron-right",
        "turn-slight-left": "chevron-left",
        "turn-sharp-right": "arrow-right",
        "turn-sharp-left": "arrow-left",
        roundabout: "circle",
        destination: "flag",
      };
      return iconMap[type] || "arrow-up";
    },

    // Rotate map to show navigation direction
    rotateMap(bearing) {
      const mapContainer = this.map.getContainer();
      if (mapContainer) {
        mapContainer.style.transform = `rotate(${-bearing}deg)`;
        mapContainer.style.transformOrigin = "center";
        mapContainer.style.transition = "transform 0.5s ease-out";
      }
    },

    updateNavigationStep() {
      const userPosition = this.userPosition;
      if (!userPosition.lat || !userPosition.lng || this.instructions.length === 0 || this.gpsStatus == "GPS Not Available") return;

      const currentStep = this.instructions[this.currentStepIndex];
      if (!currentStep || !currentStep.maneuver || !currentStep.maneuver.location) return;

      const distance = this.calculateDistance(userPosition.lat, userPosition.lng, currentStep.maneuver.location[1], currentStep.maneuver.location[0]);

      this.distanceToNext = distance;

      // Calculate bearing for map rotation
      const bearing = this.calculateBearing(userPosition.lat, userPosition.lng, currentStep.maneuver.location[1], currentStep.maneuver.location[0]);

      // Rotate map to show direction
      this.rotateMap(bearing);

      if (distance < 15 && this.currentStepIndex < this.instructions.length - 1) {
        this.currentStepIndex++;
        this.currentInstruction = this.instructions[this.currentStepIndex];
      }
    },

    handlePositionUpdate() {
      const position = this.coords;
      if (!position || !position.latitude || !position.longitude) return;

      const { latitude, longitude, speed } = position;
      this.userSpeed = speed || 0;
      this.gpsStatus = "GPS Active";

      this.sendPositionToBackend(latitude, longitude, speed);

      if (!this.routingControl && this.selectedOrders.length > 0) {
        this.initializeRouting();
      }

      let displayPosition = { lat: latitude, lng: longitude };
      if (this.routePolyline) {
        displayPosition = this.snapToRoute(latitude, longitude, this.routePolyline.getLatLngs());
      }

      if (this.userMarker && this.map) {
        this.map.removeLayer(this.userMarker);
        this.userMarker = null;
      }

      const userIcon = L.divIcon({
        className: "user-location-marker",
        html: '<div class="user-dot"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      this.userMarker = L.marker([displayPosition.lat, displayPosition.lng], {
        icon: userIcon,
        zIndexOffset: 1000,
      }).addTo(this.map);

      const mapBounds = this.map.getBounds();
      const userLatLng = L.latLng(displayPosition.lat, displayPosition.lng);

      if (!mapBounds.contains(userLatLng)) {
        this.map.panTo(userLatLng, { animate: true, duration: 1 });
        // Set closer zoom level for better navigation
        if (this.map.getZoom() < 18) {
          this.map.setZoom(18);
        }
      }

      this.updateNavigationStep();
    },

    sendPositionToBackend(latitude, longitude, speed = 0) {
      let update = latitude.toFixed(4) != this.lastLat || longitude.toFixed(4) != this.lastLong ? true : false;

      if (this.channel && this.channel.state === "joined" && update) {
        this.lastLat = latitude.toFixed(4);
        this.lastLong = longitude.toFixed(4);
        this.channel.push("driver:update", {
          latitude,
          longitude,
          timestamp: Date.now(),
          speed,
          driver_id: localStorage.getItem("station") || "unknown",
        });
      }
    },

    handleGeoError(error) {
      console.error("GPS Error:", error);
      if (error.code === 1) {
        this.gpsStatus = "GPS Permission Denied";
      } else if (error.code === 2) {
        this.gpsStatus = "GPS Position Unavailable";
      } else if (error.code === 3) {
        this.gpsStatus = "GPS Timeout";
      } else {
        this.gpsStatus = "GPS Error";
      }
    },

    initializeMap() {
      if (this.selectedOrders.length > 0 && this.selectedOrders[0].location == "city") {
        this.map = L.map("map").setView(this.cityCoords, 20);
      } else {
        this.map = L.map("map").setView(this.nevCoords, 20);
      }

      // bottom right but it can be removed if wanted
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(this.map);
    },

    getWaypoints() {
      if (this.selectedOrders.length === 0) return [];
      const waypoints = this.selectedOrders.map((order, idx) => ({ lat: order.delivery_data.coordinates.lat, lng: order.delivery_data.coordinates.lon, idx: idx, order: order }));
      return waypoints;
    },

    markAsDelivered() {
      let theOrder = this.currentOrder;
      theOrder.events.delivered_at = new Date().toISOString();
      theOrder.delivery_data.status = "delivered";
      this.channel.push("delivery:update", theOrder);
      this.showOrderModal = false;
    },

    checkIfDriverAtCollectionPoint() {
      if (!this.currentOrder) return;
      let userPosition = this.userPosition;
      let collectionPoint = this.currentOrder.location == "city" ? this.cityCoords : this.nevCoords;
      let distanceToCollection = this.calculateDistance(userPosition.lat, userPosition.lng, collectionPoint.lat, collectionPoint.lng);
      if (distanceToCollection < 20) {
        return { lat: userPosition.lat, lng: userPosition.lng, atStore: true };
      }
      return { lat: collectionPoint.lat, lng: collectionPoint.lng, atStore: false };
    },

    initializeRouting() {
      let delDestinations = this.getWaypoints();
      let whereIsDriver = this.currentOrder ? this.checkIfDriverAtCollectionPoint() : null;
      let waypoints = [
        L.latLng(this.userPosition.lat, this.userPosition.lng), // Start at user position
      ];

      //add store as waypoint if driver isn't at store
      if (whereIsDriver && !whereIsDriver.atStore) {
        waypoints.push(L.latLng(whereIsDriver.lat, whereIsDriver.lng));
      }

      waypoints.push(...delDestinations.map((wp) => L.latLng(wp.lat, wp.lng)));

      this.routingControl = L.Routing.control({
        waypoints: waypoints,
        router: L.Routing.osrmv1({
          serviceUrl: "https://routing.beamy.nz/route/v1",
          profile: "driving",
        }),
        show: false,
        addWaypoints: false,
        routeWhileDragging: true,
        createMarker: (i, waypoint, n) => {
          let iconHtml = `${i + 1}`;

          let marker = L.marker(waypoint.latLng, {
            icon: L.divIcon({
              className: "custom-marker",
              html: `<div style="background:#0ea5e9;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:bold">${iconHtml}</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            }),
          });

          marker.on("click", () => {
            if (i > 0) {
              let orderIndex;

              if (!whereIsDriver.atStore && i === 1) {
                return;
              } else if (!whereIsDriver.atStore) {
                orderIndex = i - 2;
              } else {
                orderIndex = i - 1;
              }

              if (orderIndex >= 0 && delDestinations[orderIndex] && delDestinations[orderIndex].order) {
                const order = delDestinations[orderIndex].order;
                this.currentOrder = order;
                this.showOrderModal = true;
              }
            }
          });

          return marker;
        },
      });

      this.routingControl.on("routesfound", (e) => {
        const routes = e.routes;
        const route = routes[0];

        this.routePolyline = L.polyline(route.coordinates.map((coord) => [coord.lat, coord.lng]));

        this.instructions = route.instructions || [];

        if (this.instructions.length > 0) {
          this.currentInstruction = this.instructions[0];
          this.currentStepIndex = 0;
        }
      });

      this.routingControl.addTo(this.map);
    },

    cleanupMap() {
      if (this.routingControl && this.map) {
        this.map.removeControl(this.routingControl);
        this.routingControl = null;
      }

      if (this.userMarker && this.map) {
        this.map.removeLayer(this.userMarker);
        this.userMarker = null;
      }

      if (this.routePolyline && this.map) {
        this.map.removeLayer(this.routePolyline);
        this.routePolyline = null;
      }

      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    },
  },
  mounted() {
    this.initializeMap();
    this.resumeTracking();
  },
  beforeUnmount() {
    this.pauseTracking();
    this.cleanupMap();
  },
  watch: {
    coords: {
      handler(newCoords) {
        if (newCoords) {
          this.handlePositionUpdate();
        }
      },
      deep: true,
    },
    geoError: {
      handler(error) {
        if (error) {
          this.handleGeoError(error);
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";

.navigation-container {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.header {
  position: fixed;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  gap: 1rem;
  width: 95%;

  .instruction-panel {
    z-index: 1000;
    background: var(--tertiary-colour);
    border-radius: 0.5rem;
    padding: 1rem;
    box-shadow: var(--box-shadow);
    display: flex;
    align-items: center;
    justify-content: center;

    .instruction-icon {
      color: var(--text-colour);
      min-width: 60px;
      text-align: center;
    }

    .instruction-text {
      h3 {
        margin: 0;
        font-size: 1.25rem;
        color: var(--text-colour);
      }

      p {
        margin: 0;
        color: var(--action-colour);
        font-weight: 600;
      }
    }
  }

  .status-panel {
    z-index: 1000;
    background: var(--tertiary-colour);
    box-shadow: var(--box-shadow);
    border-radius: 0.5rem;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .gps-status {
      padding: 0.25rem 0.25rem;
      border-radius: 0.25rem;
      color: white;
    }

    &.gps-active {
      background-color: var(--success-colour); // Green for active GPS
    }

    &.gps-inactive {
      background-color: var(--warning-colour); // Red for inactive GPS
    }

    .status-item {
      span {
        color: var(--text-colour);

        font-size: 0.8rem;
        font-weight: 500;
      }
    }
  }
}

.map {
  flex: 1;
  min-height: 72.5vh;
  min-width: 95vw;
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  background-color: var(--tertiary-colour);
  top: 7rem;

  :deep(.leaflet-routing-container) {
    display: none;
  }

  :deep(.user-location-marker) {
    background: transparent;
    border: none;
  }

  :deep(.user-dot) {
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-bottom: 35px solid var(--primary-colour);
    filter: drop-shadow(0 5px 10px rgba(0, 0, 0));
  }
}
</style>
